import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Star } from "lucide-react";
import { PlanCheckoutButton } from "../PlanCheckoutButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { formatCurrency } from "@/utils/formatters";
import { supabase } from "../../lib/supabase";

interface Plan {
  id: string;
  title: string;
  price: number;
  description: string;
  expiration_days: number;
  expiration_minutes: number;
  is_active: boolean;
  device_limit?: number;
  is_custom?: boolean;
  whatsapp_url?: string;
  features?: string[];
  created_at: string;
}

interface UserPlanInfo {
  plan_name?: string;
  subscription_plan?: string;
  subscription_status?: string;
  subscription_end_date?: string;

}

interface ChangePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan?: string;
}

export const ChangePlanModal = ({ isOpen, onClose, currentPlan: propCurrentPlan }: ChangePlanModalProps) => {
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const [userPlanInfo, setUserPlanInfo] = useState<UserPlanInfo | null>(null);
  const customerEmail = user?.email || ""; // Usar email real do usuário logado

  // Plano atual: priorizar dados do banco sobre prop
  const currentPlan = userPlanInfo?.subscription_plan || userPlanInfo?.plan_name || propCurrentPlan;

  // Verificar se o plano está ativo (não expirado)
  const isPlanActive = useCallback(() => {
    console.log('🔍 ChangePlanModal - Verificando status:', userPlanInfo);

    if (!userPlanInfo?.subscription_status) return false;

    // Se explicitamente ativo
    if (userPlanInfo.subscription_status === 'active') {
      // Se tiver data de fim, verificar se ainda é válida
      if (userPlanInfo.subscription_end_date) {
        const endDate = new Date(userPlanInfo.subscription_end_date);
        const now = new Date();
        const isActive = now.getTime() <= endDate.getTime();
        console.log(`📅 Verificação de validade: Agora (${now.toISOString()}) <= Fim (${endDate.toISOString()}) = ${isActive}`);
        return isActive;
      }
      // Se status é active e não tem data de fim, considerar ativo
      return true;
    }

    return false;
  }, [userPlanInfo]);

  const fetchUserPlanInfo = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (!error && profileData) {
        console.log('📋 ChangePlanModal - Dados do plano do usuário:', profileData);
        setUserPlanInfo(profileData);
      }
    } catch (err) {
      console.error('Erro ao buscar info do plano:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    const fetchPlansAndUserProfile = async () => {
      if (!isOpen) return;

      try {
        setLoading(true);

        // Buscar planos
        const { data, error } = await supabase
          .from('plans')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (error) {
          console.error('❌ Erro ao buscar planos:', error);
          throw error;
        }

        console.log('📋 Planos carregados:', data);

        // Buscar perfil do usuário para verificar se já usou Trial e dados do plano
        if (user?.id) {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();

          if (!profileError && profileData) {
            setHasUsedTrial(profileData.has_used_trial === true);
            setUserPlanInfo(profileData);
            console.log('📋 Usuário já usou Trial:', profileData.has_used_trial);
            console.log('📋 Plano atual do usuário:', profileData.subscription_plan || profileData.plan_name);
          }
        }

        // Ordenar planos na ordem específica: Trial → Start → Plus → Master → Personalite
        const planOrder: Record<string, number> = {
          'trial': 0,
          'start': 1,
          'plus': 2,
          'master': 3,
          'personalite': 4,
          'personalizado': 4,
          'custom': 5
        };

        const getPlanOrder = (title: string): number => {
          const lowerTitle = title?.toLowerCase() || '';
          for (const [key, order] of Object.entries(planOrder)) {
            if (lowerTitle.includes(key)) return order;
          }
          return 99; // Planos não reconhecidos vão para o final
        };

        const sortedPlans = (data || []).sort((a, b) => {
          return getPlanOrder(a.title) - getPlanOrder(b.title);
        });

        setPlans(sortedPlans);
      } catch (err) {
        console.error('Erro ao buscar planos:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlansAndUserProfile();
  }, [isOpen, user?.id]);

  // Escutar eventos de atualização após pagamento
  useEffect(() => {
    const handlePlanUpdate = () => {
      console.log('🔄 ChangePlanModal - Recebeu evento de atualização de plano');
      fetchUserPlanInfo();
    };

    window.addEventListener('userPlanRefresh', handlePlanUpdate);
    window.addEventListener('profileRefresh', handlePlanUpdate);
    window.addEventListener('planActivated', handlePlanUpdate);

    return () => {
      window.removeEventListener('userPlanRefresh', handlePlanUpdate);
      window.removeEventListener('profileRefresh', handlePlanUpdate);
      window.removeEventListener('planActivated', handlePlanUpdate);
    };
  }, [fetchUserPlanInfo]);

  const handlePlanSelect = (planName: string) => {
    setSelectedPlan(planName);
  };

  const handleClose = () => {
    setSelectedPlan(null);
    onClose();
  };

  // Função para obter features do plano
  const getPlanFeatures = (plan: Plan) => {
    // Para TODOS os planos (exceto trial), usar apenas dados da coluna features do banco
    if (!plan.title.toLowerCase().includes('trial')) {
      if (plan.features && Array.isArray(plan.features) && plan.features.length > 0) {
        return plan.features;
      } else {
        return ["Recursos não configurados"];
      }
    }

    // Apenas Trial usa features hardcoded
    return [
      "1 aparelho",
      "1 cupom",
      "Acesso total a todos os recursos"
    ];
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl w-[95vw] sm:w-[90vw] lg:w-[80vw] flex flex-col p-0 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 mx-auto rounded-xl shadow-xl border bg-white max-h-[90vh]">
        <DialogHeader className="px-5 sm:px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="space-y-1">
              <DialogTitle className="text-lg font-bold">
                Planos de Assinatura
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-3 sm:p-5 lg:p-6">
          {/* Plano Atual ou Status */}
          <div className={`mb-4 p-3 rounded-lg border ${currentPlan && isPlanActive() ? 'bg-green-50 border-green-200' : 'bg-muted/50 border-primary/20'}`}>
            <div className="flex items-center justify-between">
              <div>
                {currentPlan && isPlanActive() ? (
                  <>
                    <h3 className="font-semibold text-base text-green-800">Seu Plano Atual</h3>
                    <p className="text-sm text-green-600 font-medium">{currentPlan}</p>
                  </>
                ) : currentPlan && !isPlanActive() ? (
                  <>
                    <h3 className="font-semibold text-base text-red-800">Plano Expirado</h3>
                    <p className="text-sm text-red-600">{currentPlan} - Renove seu plano</p>
                  </>
                ) : (
                  <>
                    <h3 className="font-semibold text-base">Nenhum Plano Ativo</h3>
                    <p className="text-sm text-muted-foreground">Escolha o melhor plano para o seu negócio</p>
                  </>
                )}
              </div>
              <Badge
                variant="secondary"
                className={
                  currentPlan && isPlanActive()
                    ? "bg-green-100 text-green-800 text-xs border border-green-300"
                    : currentPlan && !isPlanActive()
                      ? "bg-red-100 text-red-800 text-xs border border-red-300"
                      : "bg-orange-100 text-orange-600 text-xs"
                }
              >
                {currentPlan && isPlanActive() ? "Ativo" : currentPlan && !isPlanActive() ? "Expirado" : "Sem Plano"}
              </Badge>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Carregando planos...</p>
              </div>
            </div>
          ) : (
            /* Grid de Planos */
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {plans.map((plan, index) => {
                const isCurrentPlan = currentPlan && (
                  plan.title.toLowerCase() === currentPlan.toLowerCase() ||
                  plan.title === currentPlan
                );
                const isCurrentPlanActive = isCurrentPlan && isPlanActive();
                const isSelected = selectedPlan === plan.title;
                const features = getPlanFeatures(plan);

                // Verificar se é plano Trial
                const isTrialPlan = plan.title.toLowerCase().includes('trial');

                // Se o usuário tem um plano atual e esse plano NÃO é trial (ex: teste plan), 
                // ele não pode voltar para o Trial, independente se o plano atual está ativo ou expirado.
                const hasNonTrialPlan = currentPlan && !currentPlan.toLowerCase().includes('trial');

                // Trial desabilitado se: já usou trial OU tem plano não-trial vinculado
                // E também não desabilitar se o trial FOR o plano atual (para mostrar como "Plano Atual")
                const isTrialDisabled = isTrialPlan && (hasUsedTrial || hasNonTrialPlan) && !isCurrentPlan;

                return (
                  <Card
                    key={plan.id}
                    className={`relative flex flex-col h-full transition-all duration-300 ${isTrialDisabled
                      ? 'border-2 border-gray-300 bg-gray-100 cursor-not-allowed opacity-70'
                      : isCurrentPlanActive
                        ? 'border-2 border-green-500 bg-green-50 shadow-lg ring-2 ring-green-200 cursor-default'
                        : isCurrentPlan && !isPlanActive()
                          ? 'border-2 border-red-300 bg-red-50 cursor-pointer'
                          : isSelected
                            ? 'border-2 border-primary shadow-lg scale-105 cursor-pointer'
                            : 'border hover:border-primary/50 hover:shadow-md cursor-pointer'
                      }`}
                    onClick={() => !isCurrentPlanActive && !isTrialDisabled && handlePlanSelect(plan.title)}
                  >
                    {isCurrentPlanActive && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-600 text-white px-3 py-1 text-xs font-bold z-10 shadow-md">
                        ✓ Plano Ativo
                      </Badge>
                    )}
                    {isCurrentPlan && !isPlanActive() && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 text-white px-3 py-1 text-xs font-bold z-10">
                        Expirado - Renovar
                      </Badge>
                    )}

                    {isTrialDisabled && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-500 text-white px-3 py-1 text-xs font-bold z-10">
                        Já Utilizado
                      </Badge>
                    )}

                    <CardHeader className="text-center pb-2 pt-3">
                      <CardTitle className={`text-lg ${isTrialDisabled ? 'text-gray-500' : ''}`}>{plan.title}</CardTitle>
                      <CardDescription className={`text-xs mt-1 h-8 line-clamp-2 ${isTrialDisabled ? 'text-gray-400' : ''}`}>
                        {isTrialDisabled ? 'Você já utilizou o período de teste' : plan.description}
                      </CardDescription>
                      <div className="flex items-baseline justify-center gap-1 pt-1">
                        {plan.is_custom ? (
                          <span className="text-lg font-bold text-primary">
                            Sob consulta
                          </span>
                        ) : plan.title.toLowerCase().includes('trial') ? (
                          <span className={`text-lg font-bold ${isTrialDisabled ? 'text-gray-400' : 'text-green-600'}`}>
                            GRATUITO
                          </span>
                        ) : (
                          <>
                            <span className="text-2xl font-bold">
                              {formatCurrency(plan.price)}
                            </span>
                            <span className="text-muted-foreground text-xs">/mês</span>
                          </>
                        )}
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-2 flex flex-col flex-grow">
                      <ul className="space-y-1 flex-grow">
                        {features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start gap-1.5">
                            <Check className={`h-2.5 w-2.5 flex-shrink-0 mt-0.5 ${isTrialDisabled ? 'text-gray-400' : 'text-primary'}`} />
                            <span className={`text-xs leading-tight ${isTrialDisabled ? 'text-gray-400' : 'text-muted-foreground'}`}>
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {isTrialDisabled ? (
                        <Button
                          className="w-full mt-2 py-1.5 bg-gray-400 text-white cursor-not-allowed"
                          variant="secondary"
                          disabled
                        >
                          Período de teste já utilizado
                        </Button>
                      ) : isCurrentPlanActive ? (
                        <Button
                          className="w-full mt-2 py-1.5 bg-green-100 text-green-800 border-green-300 hover:bg-green-100"
                          variant="outline"
                          disabled
                        >
                          ✓ Plano Ativo
                        </Button>
                      ) : isCurrentPlan && !isPlanActive() ? (
                        <PlanCheckoutButton
                          planId={plan.id}
                          customerEmail={customerEmail}
                          variant="destructive"
                        >
                          Renovar Plano
                        </PlanCheckoutButton>
                      ) : plan.is_custom ? (
                        <Button
                          className="w-full mt-2 py-1.5 bg-green-600 hover:bg-green-700 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (plan.whatsapp_url) {
                              window.open(plan.whatsapp_url, '_blank');
                            }
                          }}
                        >
                          Conversar no WhatsApp
                        </Button>
                      ) : (
                        <PlanCheckoutButton
                          planId={plan.id}
                          customerEmail={customerEmail}
                          variant="primary"
                        >
                          Escolher {plan.title}
                        </PlanCheckoutButton>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Informações Adicionais */}
          <div className="mt-4 p-3 bg-muted/30 rounded-lg">
            <h4 className="font-semibold text-sm mb-1">Informações Importantes</h4>
            <ul className="text-xs text-muted-foreground space-y-0.5">
              <li>• A mudança de plano será aplicada no próximo ciclo de faturamento</li>
              <li>• Você não perderá acesso aos recursos do plano atual até a próxima fatura</li>
              <li>• Diferenciais de preço serão ajustados proporcionalmente</li>
              <li>• Suporte disponível para dúvidas sobre a migração</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
